import cls from "classnames";
import dayjs from "dayjs";
import { isEqual } from "lodash";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { getFlightListAlertV2, getFlightListV2, getRouteCheck } from "@/api";
import { getUserDailyLimit } from "@/api/account";
import AlertModal, { AlertModalRefProps } from "@/components/AlertModal";
import CashFareRange from "@/components/CashFareRange";
import FilterBar from "@/components/FilterBar";
import { FilterData } from "@/components/FilterBar/typing";
import FlightList from "@/components/FlightList";
import FlightListSkeleton from "@/components/FlightList/Skeleton";
import FlightListGrid from "@/components/FlightListGrid";
import useGridPlatform from "@/components/FlightListGrid/usePlatform";
import FlightListV2 from "@/components/FlightListV2";
import IntroAlert from "@/components/IntroAlert";
import MobileSearchBar from "@/components/MobileSearchBar";
import RegMemberTips from "@/components/RegMemberTips";
import FilterRoundTrip from "@/components/RoundTripTab";
import RouteVoteDialog from "@/components/RouteVoteDialog";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import SearchBar from "@/components/SearchBar";
import Sort from "@/components/Sort";
import CabinSort from "@/components/Sort/CabinSort";
import { sortFn } from "@/components/Sort/sortConfig";
import TrackPriceCard from "@/components/TrackPriceCard";
import { AlertTypeEnum, FlightWayEnum, ListViewType, RoundTripTypeEnum } from "@/constants";
import { API_KEY } from "@/constants/config";
import { MONTHLY_MEMBERSHIP_PRICE, YEARLY_MEMBERSHIP_PRICE } from "@/constants/membership";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP } from "@/constants/programs";
import { useAlertParamsContext } from "@/context/AlertParamsContext";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useCheckSearchCondition from "@/hooks/useCheckSearchCondition";
import useEvent from "@/hooks/useEvent";
import useNavigateLogin from "@/hooks/useNavigateLogin";
import usePlatform from "@/hooks/usePlatform";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import useSearchQuery from "@/hooks/useSearchQuery";
import useThemeData from "@/hooks/useThemeData";
import useTrackPriceDateRange from "@/hooks/useTrackPriceDateRange";
import useUserInfo from "@/hooks/useUserInfo";
import { FlightItem as FlightItemType } from "@/typings";
import { createSearchQuery, parseSearchQuery } from "@/utils/searchQuery";

import DateFastForward from "./DateFastForward";
import ViewType from "./ViewType";

const BannerInsertIndex = 2;

const FlightSearch = () => {
  const modalRef = useRef<AlertModalRefProps>(null);
  const { isMobile, isPc } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkSearchCondition } = useCheckSearchCondition();
  const theme = useThemeData();

  const [regMemberTipsVisible, setRegMemberTipsVisible] = useState(false);

  const conditionParams = useRef(parseSearchQuery(searchParams));

  const [isLoading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(true);

  const [condition, setCondition] = useState<SearchConditionType>();
  const [searchData, setSearchData] = useState<FlightItemType[]>([]);
  const [filterData, setFilterData] = useState<FlightItemType[]>([]);
  const [sortKey, setSortKey] = useState(1);
  const { getUserInfo } = useUserInfo();
  const [visible, setVisible] = useState(false);

  const { alertParams, setAlertParams } = useAlertParamsContext();

  const [roundTripType, setRoundTripType] = useState<RoundTripTypeEnum>(RoundTripTypeEnum.Outbound);

  const { query } = useSearchQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOpenV2, setDialogOpenV2] = useState(false);
  const [dialogOpenV3, setDialogOpenV3] = useState(false);
  const [trackPriceOpen, setTrackPriceOpen] = useState(false);
  const [routePath, setRoutePath] = useState(undefined);

  const { isPc: isGridPc } = useGridPlatform();

  const [realTimeSearchBlock, setRealTimeSearchBlock] = useState(false);
  const [hardRealTimeSearchBlock, setHardRealTimeSearchBlock] = useState(false);

  const filterParams = useRef<FilterData>();

  const [searchCondition, setSearchCondition] = useState<SearchConditionType>();

  const trackPriceDateRange = useTrackPriceDateRange(roundTripType);

  const [openRouteRequest, setOpenRouteRequest] = useState(false);
  const store = useGlobalStoreContext();
  const showUpsell = store.show_upsell;

  const [listType, setListType] = useState(() =>
    Number(store.persistStore?.user_preference?.real_time_search_table_type ?? ListViewType.Grid)
  );

  useEffect(() => {
    if (store.load) {
      setListType(
        Number(
          store.persistStore?.user_preference?.real_time_search_table_type ?? ListViewType.Grid
        )
      );
    }
  }, [store]);

  const navigate = useNavigate();
  const { navigateLogin } = useNavigateLogin();

  // const isBreakpoint = useMediaQuery("(max-width:900px)");
  const isBreakpoint = useMediaQuery("(max-width:1100px)");

  const alertDetailPage = window.location.pathname === "/alert-detail";

  const partialDisplay = useMemo(() => {
    return !loggedIn || realTimeSearchBlock || store.persistStore?.real_time_search_block;
  }, [realTimeSearchBlock, loggedIn, store]);

  useEffect(() => {
    // 全局的alertParams用完即清除
    return () => {
      setAlertParams({});
    };
  }, [setAlertParams]);

  const appendSearchData = (data: FlightItemType[]) => {
    setSearchData(data);
  };

  const handleAlertDetail = useEvent(async (alert_id: string) => {
    try {
      const userInfo = await getUserInfo();
      await getFlightListAlertV2({ alert_id }, appendSearchData, userInfo.token);
      setLoading(false);
    } catch (error) {
      console.error("%c [  ]-162", "font-size:13px; background:pink; color:#bf2c9f;", error);
    }
  });

  const openDialog = () => {
    setDialogOpen(true);
  };

  const openDialogV2 = () => {
    setDialogOpenV2(true);
  };

  const requestRealTimeSearchBlock = useEvent(async () => {
    const res = await getUserDailyLimit();

    setRealTimeSearchBlock(res.real_time_search_block);
    setHardRealTimeSearchBlock(res.hard_real_time_search_block);
  });

  const handleSearch = useEvent(async (condition: SearchConditionType) => {
    // if (hardRealTimeSearchBlock) {
    //   // setRegMemberTipsVisible(true);
    //   return;
    // }

    requestRealTimeSearchBlock();

    // Get current path params
    setTrackPriceOpen(false);

    if (condition) {
      const errMsg = checkSearchCondition(condition);

      if (errMsg) {
        setLoading(false);
        return;
      }

      // update url path query
      const nextParams = createSearchQuery({ ...condition });
      const nextSearch = createSearchParams(nextParams).toString();

      setSearchParams(nextSearch);
      setCondition(condition);

      const date_range = { from: "", to: "" };
      let return_date_range = null;

      if (condition.flightWay === FlightWayEnum.OneWay) {
        if (condition.range) {
          date_range.from = dayjs(condition.oneWayRangeStartDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.oneWayRangeEndDate).format("YYYY-MM-DD");
        } else {
          date_range.from = dayjs(condition.oneWayDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.oneWayDate).format("YYYY-MM-DD");
        }
      } else {
        if (condition.range) {
          date_range.from = dayjs(condition.departureRangeStartDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.departureRangeEndDate).format("YYYY-MM-DD");

          return_date_range = {
            from: dayjs(condition.returnRangeStartDate).format("YYYY-MM-DD"),
            to: dayjs(condition.returnRangeEndDate).format("YYYY-MM-DD"),
          };
        } else {
          date_range.from = dayjs(condition.roundTripDepartureDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.roundTripDepartureDate).format("YYYY-MM-DD");

          return_date_range = {
            from: dayjs(condition.roundTripReturnDate).format("YYYY-MM-DD"),
            to: dayjs(condition.roundTripReturnDate).format("YYYY-MM-DD"),
          };
        }
      }

      console.log("%c [  ]-237", "font-size:13px; background:pink; color:#bf2c9f;", condition);

      // Check if the current url is prod

      const params = {
        search_type: condition.flightWay,
        pax: condition.pax,
        children: condition.children,
        cabins: condition.cabins.split("&").map((item) => item.trim()),
        departure_airports: condition.fromLocation.map((ele) => ele.iata),
        arrival_airports: condition.toLocation.map((ele) => ele.iata),
        programs: condition.programs,
        date_range,
        api_key: API_KEY,
        type: "",
        task_id: "",
        task_ct: 1,
      };

      if (return_date_range) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        params.return_date_range = return_date_range;
      }

      try {
        const userInfo = await getUserInfo();
        if (!userInfo.token) {
          setLoggedIn(false);
        }

        getRouteCheck(condition, setRoutePath);

        // 手动出发搜索按钮时缓存搜索条件到本地
        localStorage.setItem("flight_search_conditions", JSON.stringify(condition));
        await setLoading(true);
        await setSearchData([]);
        await getFlightListV2(
          params,
          appendSearchData,
          userInfo.token,
          condition,
          showUpsell,
          openDialog,
          openDialogV2,
          setLoading
        );

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  });

  const handleSearchChange = useEvent((data: SearchConditionType) => {
    setSearchCondition(data);
  });

  const filterChange = useCallback(
    (v: FlightItemType[], params: FilterData) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);

      filterParams.current = params;
    },
    [sortKey]
  );

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);

      console.log("%c [  ]-298", "font-size:13px; background:pink; color:#bf2c9f;", sortKey);
    },
    [filterData]
  );

  const getExtraData = useEvent(() => {
    const data: Record<string, any> = {};

    if (condition) {
      data.passengers = condition.pax;
      data.cabins = condition.cabins;
      data.fromLocation = condition.fromLocation;
      data.toLocation = condition.toLocation;
      data.departureDate = trackPriceDateRange[0];
      data.departureDateSec = trackPriceDateRange[1];
    }

    if (filterParams.current) {
      if (filterParams.current?.maxPoints > -1) {
        data.maxPoints = String(filterParams.current.maxPoints);
      }

      if (filterParams.current.stops) {
        data.stops = filterParams.current.stops;
      }

      if (filterParams.current.banks?.length) {
        data.banks = filterParams.current.banks?.map((key) => ({
          label: key,
          value: BANK_NAME_MAP[key],
        }));
      }

      if (filterParams.current.programs?.length) {
        data.airlines = filterParams.current.programs?.map((key) => ({
          value: key,
          label: PROGRAM_NAME_MAP.get(key),
        }));
      }

      if (filterParams.current?.tax > -1) {
        data.maxTax = filterParams.current.tax;
      }

      if (!isEqual(filterParams.current.departureTime, [0, 24])) {
        data.departureTime = filterParams.current.departureTime;
      }

      if (!isEqual(filterParams.current.arrivalTime, [0, 24])) {
        data.arrivalTime = filterParams.current.arrivalTime;
      }

      if (filterParams.current.duration !== -1) {
        data.maxDuration = Math.floor(filterParams.current.duration / 60);
      }
    }

    return data;
  });

  const trackChange = useEvent((v) => {
    setTrackPriceOpen(v);

    if (v) {
      const data = getExtraData();
      modalRef.current && modalRef.current.show(AlertTypeEnum.Flight, data);
    }
  });

  useEffect(() => {
    if (alertDetailPage && window.location.search) {
      handleAlertDetail(window.location.search.substring(10), appendSearchData);
    } else if (window.location.search && conditionParams.current) {
      setCondition(conditionParams.current);
      handleSearch(conditionParams.current);
    } else {
      setLoading(false);
    }
  }, [handleSearch]);

  useEffect(() => {
    requestRealTimeSearchBlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 计算后的原数据，处理roundtrip类型过滤
  const computedSearchData = useMemo(() => {
    let newSearchData = searchData;
    if (listType === ListViewType.Grid) {
      const idsMap = new Map<string, string>();

      newSearchData = newSearchData.filter((item) => {
        if (idsMap.has(item.ids?.flight_id)) {
          return false;
        }
        idsMap.set(item.ids?.flight_id, "1");
        return true;
      });
    }

    if (query.flightWay !== FlightWayEnum.RoundTrip) {
      return newSearchData;
    }

    return newSearchData.filter((item) => {
      return roundTripType === RoundTripTypeEnum.Return ? item.return_trip : !item.return_trip;
    });
  }, [searchData, listType, query.flightWay, roundTripType]);

  const [totalTitle, setTotalTitle] = useState("");

  useEffect(() => {
    let text = "";
    const suffix = listType === ListViewType.Grid ? " flights" : " results";
    if (computedSearchData.length > 0) {
      if (filterData.length === computedSearchData.length) {
        text = `${computedSearchData.length.toLocaleString()}` + suffix;
      } else {
        text =
          `${filterData.length.toLocaleString()}/${computedSearchData.length.toLocaleString()}` +
          suffix;
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [computedSearchData, filterData, listType]);

  const isNeedTopData = useMemo(() => {
    return query.targetId && filterData.find((item) => item.id === query.targetId);
  }, [filterData, query]);

  const topTargetDataSearchList = useMemo(() => {
    const target = filterData.find((item) => item.id === query.targetId);
    if (query.targetId && target) {
      return [
        {
          ...target,
          isTop: true,
        },
        ...filterData.filter((item) => item.id !== query.targetId),
      ] as FlightItemType[];
    }

    return filterData;
  }, [filterData, query]);

  useEffect(() => {
    if (!isLoading && query.targetId && !isNeedTopData) {
      setDialogOpenV3(true);
    }
  }, [isLoading, query, isNeedTopData]);

  const showPrompt = (simpler = false) => {
    return (
      <div
        className={
          isPc
            ? "mx-[10px] mt-[15px] items-center justify-center text-center"
            : "mx-[10px] mt-[15px] items-center"
        }
      >
        <Typography variant="h6" color="textPrimary">
          Welcome!{" "}
          <Typography
            component={Link}
            onClick={() => navigateLogin("/signup")}
            color="primary"
            variant="h6"
            className="inline"
          >
            Save thousands and hours
          </Typography>{" "}
          with a{" "}
          <Typography
            component={Link}
            onClick={() => navigateLogin("/signup")}
            color="primary"
            variant="h6"
            className="inline"
          >
            free
          </Typography>{" "}
          account, necessary to fully enjoy real-time search across all programs.{" "}
          {!simpler && (
            <Typography variant="h6" color="textPrimary">
              <Typography
                component={Link}
                color="primary"
                variant="h6"
                className="inline"
                onClick={() => navigateLogin("/signup")}
              >
                Sign up
              </Typography>{" "}
              now to enjoy your search! Or try our{" "}
              <Typography
                component={Link}
                href="/routes"
                color="primary"
                variant="h6"
                className="inline"
              >
                Panorama
              </Typography>{" "}
              feature for{" "}
              <Typography color="primary" variant="h6" className="inline">
                free
              </Typography>
              .
            </Typography>
          )}
        </Typography>

        <div
          className={
            isPc
              ? "flex items-center justify-center text-center w-[100%]" +
              (simpler ? " mb-[20px]" : "")
              : "flex items-center w-[100%]"
          }
        >
          <Typography
            className="flex items-center justify-center "
            variant="body1"
            color="textPrimary"
          >
            Thanks for choosing us for your award travel hunting! 😃 🚀 ✨
            {/* <TagFacesIcon sx={{ margin: "auto", marginLeft: "10px" }} /> */}
          </Typography>
        </div>
      </div>
    );
  };

  const showLimit = (isLoading = false) => {
    let beforeCode, afterCode, title, couponCode, expireSec;

    if (store.promotion) {
      const text = store.promotion.text;
      couponCode = store.promotion.code;
      title = store.promotion.title;
      expireSec = (store.promotion.expire - Math.floor(Date.now() / 1000)) * 1000;
      // Split the text into two parts: before the code and after the code
      const index = text.indexOf(couponCode);
      if (index !== -1) {
        beforeCode = text.substring(0, index);
        afterCode = text.substring(index + couponCode.length);
      } else {
        beforeCode = text;
        afterCode = "";
      }
    }

    return (
      <div
        className={
          isPc
            ? "mx-[10px] items-center justify-center text-center" +
            (isLoading ? " mt-[15px] " : " mt-[5px]")
            : "mx-[10px] items-center" + (isLoading ? " mt-[15px] " : " mt-[5px]")
        }
      >
        <Typography variant="h6" color="textPrimary">
          You've reached daily search limit. Limited results will be displayed.{" "}
          {showUpsell && (
            <Typography variant="h6" color="textPrimary">
              💡Help support the development of AwardTool and unlock multiple powerful features by{" "}
              <Typography
                component={Link}
                color="primary"
                variant="h6"
                className="inline"
                onClick={() => navigateLogin("/pro")}
              >
                signing up for
              </Typography>{" "}
              a{" "}
              {/* <Typography
              component={Link}
              color="primary"
              variant="h6"
              className="inline"
            >
              PRO
            </Typography> */}
              <Button
                sx={{ fontWeight: 800, padding: 0, paddingY: 0.2, margin: 0, width: 10 }}
                variant="contained"
                size="small"
              >
                PRO
              </Button>{" "}
              {"account."}
              <Typography color="primary" variant="h6">
                {MONTHLY_MEMBERSHIP_PRICE +
                  "$ per month or " +
                  YEARLY_MEMBERSHIP_PRICE +
                  "$ per year."}
                🙌✨
              </Typography>
            </Typography>
          )}
          {store.load &&
            showUpsell &&
            (store.promotion?.code ? (
              <></>
            ) : (
              <Typography variant="h6" color="textPrimary">
                Use code{" "}
                <Typography
                  component={Link}
                  color="primary"
                  variant="h6"
                  className="inline"
                  onClick={() => navigateLogin("/signup")}
                >
                  SAVE20
                </Typography>{" "}
                for 20% off!
              </Typography>
            ))}
        </Typography>

        <div
          className={
            isPc
              ? "flex items-center justify-center text-center w-[100%]" +
              (false ? " mb-[20px]" : "")
              : "flex items-center w-[100%]"
          }
        >
          <Typography
            className="flex items-center justify-center "
            variant="body1"
            color="textPrimary"
          >
            Thanks for your support and safe travels! 😃 🚀 ✨
          </Typography>
        </div>
      </div>
    );
  };

  const renderFastForward = () => {
    return (
      <>
        {searchCondition?.flightWay === FlightWayEnum.OneWay && (
          <div className="relative flex justify-end">
            <DateFastForward disabled={isLoading} />
            {showUpsell && (
              <div
                className="absolute top-0 left-0 right-0 bottom-0 cursor-pointer"
                onClick={() => {
                  setVisible(true);
                }}
              />
            )}
          </div>
        )}
      </>
    );
  };

  const renderListContentStart = () => {
    if (listType === ListViewType.List) {
      return isNeedTopData ? (
        <>
          <FlightList data={topTargetDataSearchList.slice(0, 1)} />
          <div className="mt-[20px]" />
          <FlightList
            showPro={showUpsell}
            isNeedTopData
            data={
              partialDisplay
                ? topTargetDataSearchList.slice(1, 5)
                : topTargetDataSearchList.slice(1)
            }
          />
        </>
      ) : (
        <FlightList
          showPro={showUpsell}
          data={partialDisplay ? filterData.slice(0, 5) : filterData}
        />
      );
    }

    if (listType === ListViewType.Grid) {
      return (
        <FlightListGrid
          showPro={showUpsell}
          data={partialDisplay ? filterData.slice(0, 5) : filterData}
        />
      );
    }

    if (listType === ListViewType.Table) {
      return (
        <FlightListV2
          data={partialDisplay ? topTargetDataSearchList.slice(0, 5) : topTargetDataSearchList}
          needMore
        />
      );
    }
  };

  const renderListContentEnd = () => {
    if (listType === ListViewType.List) {
      return <FlightList showPro={false} data={filterData.slice(0, 35)} />;
    }

    if (listType === ListViewType.Grid) {
      return <FlightListGrid showPro={false} data={filterData.slice(0, 35)} />;
    }

    if (listType === ListViewType.Table) {
      return <FlightListV2 hiddenHead data={topTargetDataSearchList.slice(0, 35)} />;
    }

    return null;
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Real-time Search</title>
        <meta
          name="description"
          content="The fastest real-time search engine finds the best use of your points in 20 seconds."
        />
      </Helmet>
      {isMobile ? (
        <>
          {!alertDetailPage && (
            <MobileSearchBar
              onChange={handleSearchChange}
              data={condition}
              onSearch={handleSearch}
              isLoading={isLoading}
            />
          )}
          <div className="px-[10px] mt-[20px]">
            <FilterBar
              initValues={alertParams}
              dataSourceList={computedSearchData}
              onChange={filterChange}
              roundTripType={roundTripType}
              listViewType={listType}
              loading={isLoading}
            />
            {query.flightWay === FlightWayEnum.RoundTrip ? (
              <FilterRoundTrip value={roundTripType} onChange={setRoundTripType} />
            ) : null}
            {/* <div className="mt-[20px]">{alertDetailPage ? <IntroAlert /> : <IntroFlights />}</div> */}
            <div className="mt-[20px]">{alertDetailPage ? <IntroAlert /> : null}</div>
          </div>
        </>
      ) : (
        <div>
          {!alertDetailPage && (
            <Box className="pb-[0px]" sx={{ boxShadow: 0 }}>
              <div className="max-w-[1280px] mx-auto px-[10px]">
                <SearchBar
                  onChange={handleSearchChange}
                  onSearch={handleSearch}
                  withSearchBtn
                  isLoading={isLoading}
                  getExtraData={getExtraData}
                />
              </div>
            </Box>
          )}

          <div className="max-w-[1280px] justify-center mx-auto px-[10px] my-[20px]">
            <FilterBar
              initValues={alertParams}
              dataSourceList={computedSearchData}
              onChange={filterChange}
              roundTripType={roundTripType}
              listViewType={listType}
              loading={isLoading}
            />
            {query.flightWay === FlightWayEnum.RoundTrip ? (
              <div className="flex items-center justify-center pt-[5px] pb-[0px]">
                <FilterRoundTrip value={roundTripType} onChange={setRoundTripType} />
              </div>
            ) : null}
          </div>

          <div className="max-w-[1280px] mx-auto px-[10px]">
            {/* {alertDetailPage ? <IntroAlert /> : <IntroFlights />} */}
            {alertDetailPage ? <div className="mt-[-12px]"><IntroAlert /></div> : null}
          </div>
        </div>
      )}

      <div className="max-w-[1280px] mx-auto">
        {!alertDetailPage && isMobile && (
          <div className="mt-[-5px] mx-[10px]">
            <TrackPriceCard
              dateRange={trackPriceDateRange}
              value={trackPriceOpen}
              onChange={trackChange}
              onRouteRequest={() => setOpenRouteRequest(true)}
            />
          </div>
        )}

        {isLoading ? (
          <div className={alertDetailPage ? "mt-[20px] mx-[10px] mb-[25px]" : "mt-[-12px] mx-[10px] mb-[20px] mt-[15px]"} >
            <LinearProgress />
          </div>
        ) : null}
        {!loggedIn && showPrompt()}
        {loggedIn && partialDisplay && showLimit(isLoading)}
        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            <Typography color="textSecondary">No options matching your filters</Typography>
            <Typography color="textSecondary">
              Consider modify your filter setting or search criteria
            </Typography>
            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px]">
            {filterData.length ? (
              <>
                <Grid
                  container
                  className={
                    isMobile ? "w-full mt-[7px] pb-[10px]" : (alertDetailPage ? "w-full mt-[15px] pt-[0px] pb-[10px]" : "  w-full pt-[0px] mt-[-10px] pb-[10px]")
                  }
                  alignItems="center"
                >
                  <Grid item xs={3.5} md={4}>
                    <div className="flex items-center flex-1">
                      <Typography color="textPrimary" className="!mr-[10px]">
                        {totalTitle}
                      </Typography>
                      {isPc ? <ViewType value={listType} onChange={setListType} /> : null}
                    </div>
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    md={3}
                    sx={{
                      ...(isBreakpoint
                        ? {
                          textAlign: "center",
                        }
                        : {}),
                    }}
                  >
                    {routePath !== undefined ? (
                      <Typography
                        component={Button}
                        onClick={() => {
                          window.open(routePath, "_blank");
                        }}
                        color="primary"
                        fontWeight={"bold"}
                        sx={{ padding: 0 }}
                      >
                        <StarPurple500Icon className="mb-[4px]" fontSize="small" />
                        {!isBreakpoint ? "Check Panorama Calendar View" : "Calendar View"}
                      </Typography>
                    ) : null}
                  </Grid>
                  <Grid item xs={3.5} md={5} sx={{ textAlign: "right" }}>
                    <div className="flex items-center justify-end">
                      {!isBreakpoint && (
                        <CashFareRange filterList={filterData} sourceList={searchData} />
                      )}
                      {/* {!isBreakpoint && !isLoading && renderFastForward()} */}
                      {!isBreakpoint && renderFastForward()}
                      <Sort value={sortKey} onChange={sortChange} listType={listType} />
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className="flex justify-between items-center w-full">
                      {isBreakpoint && (
                        <>
                          <div>
                            <CashFareRange filterList={filterData} sourceList={searchData} />
                          </div>
                          {/* {!isLoading && renderFastForward()} */}
                          {renderFastForward()}
                        </>
                      )}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    {isMobile ? <ViewType value={listType} onChange={setListType} /> : null}
                  </Grid>
                </Grid>

                {listType === ListViewType.Grid && isGridPc && (
                  <CabinSort value={sortKey} onChange={sortChange} />
                )}

                {renderListContentStart()}
              </>
            ) : null}

            {filterData.length > 1 && !loggedIn && showPrompt(true)}

            {filterData.length > BannerInsertIndex && partialDisplay && (
              <div
                className={cls(
                  "relative after:content-[''] after:absolute after:inset-0",
                  theme.isDarkMode
                    ? "after:backdrop-blur-sm after:bg-[#121212]/10"
                    : "after:backdrop-blur-sm after:bg-white/30"
                )}
              >
                {renderListContentEnd()}
              </div>
            )}

            {isLoading ? (
              <FlightListSkeleton
                showPro={showUpsell}
                count={partialDisplay ? Math.min(1, filterData.length) : filterData.length}
              />
            ) : null}
          </div>
        )}
      </div>

      <AlertModal ref={modalRef} onReset={() => setTrackPriceOpen(false)} />

      <RouteVoteDialog
        open={openRouteRequest}
        onOpen={() => setOpenRouteRequest(false)}
        condition={{
          fromLocation: condition?.fromLocation || [],
          toLocation: condition?.toLocation || [],
          programs: [],
        }}
      />
      <Dialog
        onClose={() => {
          setDialogOpen(false);
        }}
        open={dialogOpen}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Search Condition Error</Typography>
        </DialogTitle>
        <DialogContent dividers className="pt-[20px] !px-[20px]">
          Search condition is incorrect, please check your search condition.
        </DialogContent>
      </Dialog>

      <Dialog
        onClose={() => {
          setDialogOpenV2(false);
        }}
        open={dialogOpenV2}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Searching Too Frequently</Typography>
        </DialogTitle>
        <DialogContent className="pt-[20px] !px-[20px]">
          It seems you've performed an excessive number of searches within a brief timeframe. Please
          take a short rest {":)"}. Thanks for choosing us for your award travel hunting!
        </DialogContent>
      </Dialog>

      {/* <Dialog
        onClose={() => {
          setDialogOpenV3(false);
        }}
        open={dialogOpenV3}
        sx={{
          maxWidth: "600px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogContent className="pt-[20px] !px-[20px]">
          Oops, the flight you saw in Panorama is no longer available😵. Award flight availability
          can change quickly, please peruse our real-time search results to discover the best
          alternative flights 😃.
        </DialogContent>
      </Dialog> */}

      <Dialog
        open={visible}
        onClose={() => setVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography color="primary" fontSize={18} fontWeight={600}>
            PRO feature
          </Typography>
          <Typography fontSize={16}>
            The fast-forward button for real-time searches enables quicker and more flexible
            navigation to find your target award flight availability in seconds. Save even more
            money and time with us!
          </Typography>
          <Typography color="primary" fontSize={16} fontWeight={800}>
            Upgrade to PRO for much easier award travel planning and access to numerous advanced
            features.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisible(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <RegMemberTips open={regMemberTipsVisible} onClose={() => setRegMemberTipsVisible(false)} />
      <ScrollTopBar />
    </>
  );
};

export default FlightSearch;
